/**
 * SecurityGate 统一令牌管理
 *
 * 集中所有 dev token 硬编码，生产环境从 auth store / env 读取真实令牌。
 * 替代散落在各组件的 'dev_bypass_token' / 'OMNI_CORE_ADMIN_MOCK_TOKEN' 字面量。
 *
 * 安全原则:
 * - DEV_ONLY_TOKEN 仅用于开发/测试环境，生产环境为空字符串（触发真实鉴权）
 * - 真实令牌从 omniAuthStore / localStorage('auth_token') 读取
 * - 所有 SecurityGate 构造走此模块，杜绝散落硬编码
 */

const isDev = import.meta.env?.DEV ?? false;
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

/**
 * 开发期 bypass token — 仅 DEV 模式有效，生产为空（强制真实鉴权）
 */
export const DEV_ONLY_TOKEN = isDev ? 'dev_bypass_token' : '';

/**
 * 获取当前有效令牌（真实优先，开发降级）
 */
export function getSecurityToken(): string {
  // 1. 真实令牌 (auth store / localStorage)
  if (typeof localStorage !== 'undefined') {
    const real = localStorage.getItem('auth_token');
    if (real && real !== 'OMNI_CORE_ADMIN_MOCK_TOKEN') return real;
  }
  // 2. 开发降级 (仅 DEV)
  return DEV_ONLY_TOKEN;
}

/**
 * 构造标准 SecurityGate 对象 (director_* 命令用)
 */
export function buildSecurityGate(perms: string[] = ['director:admin', 'director:view']) {
  return {
    grantedPerms: perms,
    // [2026-09-18] 原为恒 `true`。本机 IPC 路径（`verify_local`）**不读这个字段**，
    // 只有 s2s/MCP 的 HMAC 路径（`verify`）才看它 —— 那里要求真实令牌，恒 true 会让
    // "解锁态"看起来是构造器的默认值。web_console 版早已按此改成 false，这里对齐。
    isOpUnlocked: false,
    timestamp: Date.now(),
    tokenHash: getSecurityToken(),
  };
}

/**
 * 构造 REST API 鉴权头
 */
export function buildAuthHeaders(): Record<string, string> {
  const token = getSecurityToken();
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
               : { 'Content-Type': 'application/json' };
}

// ─── Projection UI route guard helpers ────────────────────────────────

const isBrowser = typeof window !== 'undefined';

const normalizeRoutePath = (path: string) => {
  if (!path) return '/';
  const clean = path.trim();
  if (clean === '/') return '/';
  return clean.replace(/\/+$/, '') || '/';
};

export function readActiveProfile(): string {
  if (!isBrowser) return '';
  return (
    window.localStorage.getItem('active_profile')?.trim() ||
    window.localStorage.getItem('123ALL_ACTIVE_PROFILE')?.trim() ||
    ''
  );
}

export function readProjectionUiRoutes(): string[] {
  if (!isBrowser) return [];
  const raw = window.localStorage.getItem('123all_projection_ui_routes');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((p) => normalizeRoutePath(String(p || '')))
      .filter((p) => p.startsWith('/'));
  } catch {
    return [];
  }
}

export function isProjectionGuardEnabled(): boolean {
  if (!isBrowser) return false;
  const raw = (
    window.localStorage.getItem('123all_projection_route_guard') ||
    window.localStorage.getItem('123ALL_PROJECTION_ROUTE_GUARD') ||
    'true'
  )
    .trim()
    .toLowerCase();
  return !(raw === '0' || raw === 'false' || raw === 'off' || raw === 'no');
}

export function isRouteAllowedByProjection(path: string, allowedRoutes: string[]): boolean {
  const target = normalizeRoutePath(path);
  return allowedRoutes.some((route) => {
    const allowed = normalizeRoutePath(route);
    if (allowed === '/') return target === '/';
    return target === allowed || target.startsWith(`${allowed}/`);
  });
}
